import {Modal, UploadFile} from 'antd';
import {DocumentType, useCreateDocumentMutation, useUploadDocumentMutation} from 'api/generated';
import {documentsQuery} from 'api/queries';
import {AnchorLink, FileInput} from 'components';
import {Formik} from 'formik';
import {Checkbox, Form, FormItem, Input, Select} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {getContentTypeIcon} from 'lib/contentTypeIcons';
import {documentTypeLabels} from 'lib/labels';
import React, {useState} from 'react';
import * as yup from 'yup';

type FormValues = {
  name?: string;
  type?: DocumentType;
  file?: UploadFile;
  internal?: boolean;
  note?: string;
};

const validationSchema = yup.object().shape({
  type: yup.string().required(),
  file: yup.mixed().required(),
  name: yup.string(),
  note: yup.string(),
  internal: yup.boolean()
});

const initialValues: FormValues = {
  name: undefined,
  type: undefined,
  file: undefined,
  note: undefined,
  internal: false
};

export const UploadDocument: React.FC<{accountId: string}> = ({accountId}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [createDocument] = useCreateDocumentMutation();
  const [uploadDocument] = useUploadDocumentMutation();

  const handleSubmit = async (
    {type, file, name, internal, note}: FormValues,
    {setSubmitting}: FormikHelpers<FormValues>
  ) => {
    if (!type || !file) return;
    setSubmitting(true);
    try {
      const {data: createData} = await createDocument({
        variables: {input: {type, accountId, name, internal, note}},
        updateQuery: {query: documentsQuery, variables: {accountId}}
      });
      const id = createData?.createDocument?.id;
      if (!id) return false;
      const {data: uploadData} = await uploadDocument({
        variables: {id, contentType: file.type!, accountId}
      });
      const url = uploadData?.uploadDocument?.uploadUrl;
      if (!url) return false;
      await fetch(url, {method: 'PUT', body: file as any});
    } finally {
      setSubmitting(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <AnchorLink onClick={() => setIsOpen(true)}>Upload document</AnchorLink>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={!isOpen}
        validationSchema={validationSchema}>
        {({submitForm, isSubmitting, resetForm, values}) => (
          <Modal
            title="Upload new document"
            okText="Upload"
            cancelText="Cancel"
            open={isOpen}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            afterClose={resetForm}
            onCancel={() => {
              setIsOpen(false);
            }}>
            <Form layout="vertical">
              <FormItem name="type" label="Document type">
                <Select name="type" showSearch placeholder="Select document type">
                  {Object.entries(documentTypeLabels).map(([key, value]) => (
                    <Select.Option key={key} value={key}>
                      {value}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>
              <FormItem name="name" label="Name">
                <Input name="name" placeholder="Custom document name" />
              </FormItem>
              <FormItem name="note" label="Admin note">
                <Input.TextArea name="note" placeholder="Custom admin note" rows={2} />
              </FormItem>
              <FormItem name="internal">
                <Checkbox name="internal">Internal document (only visible to admins)</Checkbox>
              </FormItem>
              <FormItem name="file" label="Document file">
                <FileInput
                  name="file"
                  useRawFile
                  disabled={!values.type}
                  iconRender={(file) => getContentTypeIcon(file.type)}
                  accept={['application/pdf', 'image/jpeg', 'image/png', 'image/tiff'].join(',')}
                />
              </FormItem>
              <input type="submit" value="submit" hidden />
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
